import {
  isDefined,
  Loadable,
  OrderExchangeData,
  useOrderExchangeCountdownCommon,
  useOrderExchangeErrorsCommon,
} from '@eih-netbroker/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as CurrencyExchangeIcon } from '../../../../../assets/icons/exchange_loop_blue.svg';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { CountdownBar } from '../../../../../shared/components/countdown-bar/CountdownBar';
import { getRemainingSecondsLabel } from '../../../../../shared/components/countdown-bar/countdownBarUtils';
import { FeedbackBox } from '../../../../../shared/components/feedback-box/FeedbackBox';
import { useAccounts } from '../../../../../shared/services/useAccounts';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import classes from './OrderCurrencyExchange.module.scss';
import { OrderCurrencyExchangeCurrencySelector } from './OrderCurrencyExchangeCurrencySelector';
import { OrderCurrencyExchangeErrorState } from './OrderCurrencyExchangeErrorState';
import { OrderCurrencyExchangePaymentModeSelector } from './OrderCurrencyExchangePaymentModeSelector';
import { OrderCurrencyExchangeSummary } from './OrderCurrencyExchangeSummary';
import { OrderCurrencyExchangeSwitch } from './OrderCurrencyExchangeSwitch';
import { OrderCurrencyExchangeUnavailable } from './OrderCurrencyExchangeUnavailable';

type OrderCurrencyExchangeProps = {
  disabled: boolean;
  exchangeData?: OrderExchangeData;
  orderRequestLoading: boolean;
  shouldUseCountdownBarPlaceholder: boolean;
  sourceComponent: OrderFormSourceComponent;
};

const getConfirmedNotEnoughCashFlag = (exchangeData?: OrderExchangeData): boolean | undefined => {
  if (!isDefined(exchangeData)) {
    return false;
  }

  if (!Loadable.hasValue(exchangeData.conditions)) {
    return undefined;
  }

  if (!exchangeData.conditions.every(condition => condition.isSatisfied)) {
    return false;
  }

  if (!Loadable.hasValue(exchangeData.funding)) {
    return undefined;
  }

  return exchangeData.funding.missingAmount > 0;
};

export const OrderCurrencyExchange = ({
  disabled,
  exchangeData,
  orderRequestLoading,
  shouldUseCountdownBarPlaceholder,
  sourceComponent,
}: OrderCurrencyExchangeProps) => {
  const { translation } = useLocale();
  const { isMultipleAccountSelected } = useAccounts();
  const [lastConfirmedNotEnoughCashFlag, setLastConfirmedNotEnoughCashFlag] = useState(
    () => getConfirmedNotEnoughCashFlag(exchangeData) ?? false,
  );
  const t = translation.orderModal.currencyExchangeForm;

  const conditionsSatisfied = useMemo(() => {
    if (!isDefined(exchangeData)) {
      return false;
    }

    if (exchangeData.conditions.isLoading && !Loadable.hasValue(exchangeData.conditions)) {
      return true;
    }
    return Loadable.hasValue(exchangeData.conditions) && exchangeData.conditions.every(c => c.isSatisfied);
  }, [exchangeData]);

  const confirmedNotEnoughCashFlag = useMemo<boolean | undefined>(
    () => getConfirmedNotEnoughCashFlag(exchangeData),
    [exchangeData],
  );
  const notEnoughCashFlag = confirmedNotEnoughCashFlag ?? lastConfirmedNotEnoughCashFlag;

  const conditionsNotMetFlag = useMemo(() => {
    if (!isDefined(exchangeData)) {
      return false;
    }

    return Loadable.hasValue(exchangeData.conditions) && !exchangeData.isEnabled && !conditionsSatisfied;
  }, [exchangeData, conditionsSatisfied]);

  useEffect(() => {
    if (isDefined(confirmedNotEnoughCashFlag)) {
      setLastConfirmedNotEnoughCashFlag(confirmedNotEnoughCashFlag);
    }
  }, [confirmedNotEnoughCashFlag]);

  if (!isDefined(exchangeData)) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <div className={classes.row}>
          <CurrencyExchangeIcon className={classes.icon} />
          <div className={classes.column}>
            <span
              data-testid="order-currency-exchange-switch-label"
              className={classes.label}
            >
              {t.switchLabel}
            </span>
            {notEnoughCashFlag && !disabled && (
              <span
                data-testid="order-currency-exchange-not-enough-cash-error"
                className={classes.labelError}
              >
                {t.notEnoughCashError}
              </span>
            )}
            {conditionsNotMetFlag && !disabled && (
              <span
                data-testid="order-currency-exchange-conditions-not-met-error"
                className={classes.label}
              >
                {t.conditionsNotMetError}
              </span>
            )}
          </div>
        </div>
        <OrderCurrencyExchangeSwitch
          {...exchangeData}
          disabled={disabled || isMultipleAccountSelected}
          conditionsSatisfied={conditionsSatisfied}
          readOnly={orderRequestLoading}
        />
      </div>
      <OrderCurrencyExchangeContent
        exchangeData={exchangeData}
        orderRequestLoading={orderRequestLoading}
        shouldUseCountdownBarPlaceholder={shouldUseCountdownBarPlaceholder}
        sourceComponent={sourceComponent}
      />
    </div>
  );
};

type OrderCurrencyExchangeContentProps = {
  exchangeData: OrderExchangeData;
  orderRequestLoading: boolean;
  shouldUseCountdownBarPlaceholder: boolean;
  sourceComponent: OrderFormSourceComponent;
};

const OrderCurrencyExchangeContent = ({
  exchangeData,
  orderRequestLoading,
  shouldUseCountdownBarPlaceholder,
  sourceComponent,
}: OrderCurrencyExchangeContentProps) => {
  const { translation } = useLocale();
  const t = translation.orderModal.currencyExchangeForm;
  const errors = useOrderExchangeErrorsCommon(exchangeData);
  const { countdown } = useOrderExchangeCountdownCommon({
    exchangeRate: exchangeData.exchangeRate,
    onExchangeRateExpired: exchangeData.loadExchangeRate,
  });

  const handleRetryBalanceAndFunding = useCallback((): void => {
    exchangeData.loadFunding();
    exchangeData.loadBalances();
  }, [exchangeData]);

  if (!exchangeData.isEnabled) {
    return null;
  }

  if (errors.isExchangeUnavailable) {
    return <OrderCurrencyExchangeUnavailable />;
  }

  if (errors.isBalanceOrFundingLoadFailed) {
    return (
      <OrderCurrencyExchangeErrorState
        message={t.serverError}
        onRetry={handleRetryBalanceAndFunding}
        readOnly={orderRequestLoading}
      />
    );
  }

  return (
    <>
      <OrderCurrencyExchangePaymentModeSelector
        {...exchangeData}
        orderRequestLoading={orderRequestLoading}
        sourceComponent={sourceComponent}
      />
      {errors.isSelectedCurrencyBalanceInsufficient && (
        <FeedbackBox
          type="error"
          message={exchangeData.fundingMode === 'fullAmount' ? t.insufficientBalanceFull : t.insufficientBalanceMissing}
          isWithoutBorder
        />
      )}
      {errors.isSelectedCurrencyUnavailable && !errors.isWithoutUseableCurrencyBalance && (
        <FeedbackBox
          type="error"
          message={t.selectOtherCurrency}
          isWithoutBorder
        />
      )}
      {errors.isWithoutUseableCurrencyBalance && (
        <FeedbackBox
          type="error"
          message={t.noUseableCurrencyBalance}
          isWithoutBorder
        />
      )}
      {!errors.isWithoutUseableCurrencyBalance && (
        <OrderCurrencyExchangeCurrencySelector
          balances={exchangeData.individuallyLoadingBalances}
          selectedCurrency={exchangeData.selectedCurrency}
          hasSelectedCurrencyError={errors.isSelectedCurrencyBalanceInsufficient}
          onChange={exchangeData.setSelectedCurrency}
          orderRequestLoading={orderRequestLoading}
          sourceComponent={sourceComponent}
        />
      )}
      {isDefined(exchangeData.selectedCurrency) &&
        !exchangeData.exchangeRate.hasError &&
        !errors.isSelectedCurrencyUnavailable &&
        !errors.isWithoutUseableCurrencyBalance && (
          <OrderCurrencyExchangeSummary
            {...exchangeData}
            sourceComponent={sourceComponent}
          />
        )}

      {isDefined(exchangeData.selectedCurrency) &&
        countdown !== 'indefinite' &&
        !errors.isSelectedCurrencyUnavailable &&
        !errors.isWithoutUseableCurrencyBalance && (
          <>
            {exchangeData.exchangeRate.hasError && (
              <OrderCurrencyExchangeErrorState
                message={t.exchangeRateError}
                onRetry={exchangeData.loadExchangeRate}
                readOnly={orderRequestLoading}
              />
            )}
            <CountdownBar
              max={countdown?.durationSeconds}
              remaining={countdown?.secondsRemaining}
              getLabel={getRemainingSecondsLabel}
              shouldUseCountdownBarPlaceholder={shouldUseCountdownBarPlaceholder}
            />
          </>
        )}
    </>
  );
};
